import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { line, Color, LIST_PAGES, Size, resolvedStatusPill, t, type PackageAnswer } from '@bms2/shared';
import { Button } from '../../components/controls';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Modal } from '../../components/modals/Modal';
import { AsyncStatePanel } from '../../components/composites/AsyncStatePanel';
import { WhatsNextBanner } from '../../components/composites/WhatsNextBanner';
import { AccessDenied } from '../../shell/Fallbacks';
import { useStatusResolver } from '../../shell/StatusResolver';
import { BannerPills } from './BannerPills';
import { DocumentViewPage, type DocumentLinesDescriptor } from './DocumentViewPage';
import { PackageModal } from './PackageModal';
import { type ScreenFact } from './PageFacts';
import { ShipPackagesModal, shippedNotice } from './ShipPackagesModal';
import { del, packagePath, packageSlipPath, useApi } from './api';
import { openSheet } from './ViewPdfLink';
import styles from './DocumentPage.module.css';

/** The Package page's route, under the order it was packed against's own module. */
export const PACKAGE_ROUTE = '/packages';

type OpenWindow = 'edit' | 'ship' | 'delete' | null;

/**
 * 🔒 The Package's page (decision 1730), the owner's Zoho package screen: the
 * Packing Slip drawn as the printer draws it, with the Not Shipped or Shipped
 * band in its corner, a "From sales order" link back to the order, and Edit,
 * Ship and Print in the footer bar.
 *
 * 🔨 Edit and Ship stand on a Not Shipped package of a Released or Closed order
 * the viewer may write; the server refuses either on its own terms anyway.
 */
export function PackagePage({ packageId, apiBaseUrl = '' }: { packageId: string; apiBaseUrl?: string }) {
  const page = useApi<PackageAnswer>(packagePath(packageId), apiBaseUrl);
  const resolveStatus = useStatusResolver();
  const navigate = useNavigate();
  const [open, setOpen] = useState<OpenWindow>(null);
  const [notice, setNotice] = useState<string | null>(null);
  if (page.denied) return <AccessDenied reason={page.deniedReason} />;

  return (
    <AsyncStatePanel state={page.state} emptyAction={t('document.notFound')} onRetry={page.reload}>
      {(pkg) => {
        const orderRoute = `${LIST_PAGES.SalesOrder.route}/${pkg.salesOrderId}`;
        const moves = pkg.status === 'NotShipped' && pkg.orderReleased && !pkg.writeRefusal;
        const measure = (value: number | null, unit: string | null) => (value === null ? null : line(String(value), unit ?? ''));
        const dimensions = pkg.length === null && pkg.width === null && pkg.height === null
          ? null
          : line([pkg.length, pkg.width, pkg.height].map((v) => (v === null ? '–' : String(v))).join(' × '), pkg.dimensionUnit ?? '');
        const facts: ScreenFact[] = [
          { label: t('package.page.fromOrder'), value: <Link to={orderRoute} data-package-order={pkg.salesOrderId}>{pkg.salesOrderNumber}</Link> },
          { label: t('salesOrder.fact.account'), value: pkg.companyName, route: `/companies/${pkg.companyId}` },
          { label: t('package.window.date'), value: pkg.packageDate },
          { label: t('package.window.type'), value: pkg.packageTypeLabel },
          { label: t('package.window.netWeight'), value: measure(pkg.netWeight, pkg.weightUnit) },
          { label: t('package.window.grossWeight'), value: measure(pkg.grossWeight, pkg.weightUnit) },
          { label: t('package.page.dimensions'), value: dimensions },
          { label: t('package.page.shipment'), value: pkg.shipmentNumber },
        ];
        const lines: DocumentLinesDescriptor = {
          title: t('package.page.lines'),
          rowAttribute: 'data-package-line',
          blockAttribute: 'data-package-lines',
          money: false,
          columns: [t('package.window.quantity')],
          rows: pkg.lines.map((row) => ({ id: row.id, label: row.sku ? line(row.sku, '·', row.itemName) : row.itemName, cells: [line(String(row.quantity), row.unitName)] })),
          totals: [{ label: t('package.page.totalQuantity'), value: String(pkg.totalQuantity), attributes: { 'data-package-total': String(pkg.totalQuantity) } }],
        };
        const print = async () => {
          const opened = await openSheet(`${apiBaseUrl}${packageSlipPath(pkg.id)}`);
          if (opened.outcome !== 'opened') setNotice(`!${opened.message}`);
        };
        const footer = (
          <>
            {moves ? <Button size={Size.Medium} text={[t('package.action.edit')]} actions={[() => setOpen('edit')]} /> : null}
            {moves ? <Button size={Size.Medium} color={Color.Navy} text={[t('package.action.ship')]} actions={[() => setOpen('ship')]} /> : null}
            {moves ? <Button size={Size.Medium} text={[t('package.action.delete')]} actions={[() => setOpen('delete')]} /> : null}
            <Button size={Size.Medium} text={[t('package.action.print')]} actions={[() => void print()]} />
            {pkg.writeRefusal ? <span className={styles.refusal} data-package-refusal="true">{pkg.writeRefusal}</span> : null}
          </>
        );
        return (
          <DocumentViewPage
            documentType="Package"
            icon="item"
            title={pkg.packageNumber}
            subtitle={t('documentName.package')}
            information={facts}
            status={<BannerPills pills={[resolvedStatusPill(resolveStatus, 'PackageStatus', pkg.status)]} attribute="data-package-pills" />}
            footer={footer}
            footerAttributes={{ 'data-package-actions': moves ? 'edit ship delete print' : 'print' }}
            pageAttributes={{ 'data-package': pkg.id, 'data-package-status': pkg.status }}
            print={pkg.printDocument ?? null}
            layout={pkg.printLayout ?? null}
            sheet={pkg.printSheet ?? null}
            writeRefusal={pkg.writeRefusal ?? null}
            ribbon={pkg.status}
            ribbonStatus={pkg.status}
            pdfPath={packageSlipPath(pkg.id)}
            listRoute={orderRoute}
            apiBaseUrl={apiBaseUrl}
            lines={lines}
            whatsNext={<WhatsNextBanner documentType="Package" status={pkg.status} actions={moves ? { ship: () => setOpen('ship') } : {}} />}
            notices={notice ? <AlertBanner tone={notice.startsWith('!') ? 'error' : 'good'} message={notice.replace(/^!/, '')} action={{ label: t('modal.dismiss'), onAction: () => setNotice(null) }} /> : null}
          >
            {open === 'edit' ? (
              <PackageModal
                key={pkg.id}
                salesOrderId={pkg.salesOrderId}
                packageId={pkg.id}
                apiBaseUrl={apiBaseUrl}
                onCancel={() => setOpen(null)}
                onSaved={() => {
                  setOpen(null);
                  setNotice(t('package.saved'));
                  page.reload();
                }}
              />
            ) : null}
            {open === 'delete' ? (
              <Modal
                open
                title={t('package.delete.title')}
                onCancel={() => setOpen(null)}
                dataModal="package-delete"
                actions={[
                  {
                    kind: 'primary',
                    label: t('package.action.delete'),
                    onAct: () => void (async () => {
                      const answer = await del<unknown>(packagePath(pkg.id), apiBaseUrl);
                      setOpen(null);
                      if (!answer.ok) return setNotice(`!${answer.message}`);
                      navigate(orderRoute);
                    })(),
                  },
                  { kind: 'cancel', label: t('modal.cancel'), onAct: () => setOpen(null) },
                ]}
              >
                <p>{line(t('package.delete.body'), pkg.packageNumber)}</p>
              </Modal>
            ) : null}
            {open === 'ship' ? (
              <ShipPackagesModal
                salesOrderId={pkg.salesOrderId}
                preselected={[pkg.id]}
                apiBaseUrl={apiBaseUrl}
                onCancel={() => setOpen(null)}
                onShipped={(answer) => {
                  setOpen(null);
                  setNotice(shippedNotice(answer));
                  page.reload();
                }}
                onNewPackage={() => navigate(orderRoute)}
              />
            ) : null}
          </DocumentViewPage>
        );
      }}
    </AsyncStatePanel>
  );
}

/** The route's own reading of the package id, keyed so a second package's page never shows the first one's windows. */
export function PackagePageRoute({ apiBaseUrl = '' }: { apiBaseUrl?: string }) {
  const { id = '' } = useParams();
  return <PackagePage key={id} packageId={id} apiBaseUrl={apiBaseUrl} />;
}
